import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatService } from '../../../../service/chat.service';
import { getReplyThumbnail, getReplyMediaType, getFilesFromMessage, isMessageRedundantWithFile, isURL, isValidImageUrl, getMessageMeta, isVoiceMessage } from '../../utils/helpers.utils';

@Component({
  selector: 'app-starred-messages-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './starred-messages-panel.component.html',
  styleUrls: ['./starred-messages-panel.component.css']
})
export class StarredMessagesPanelComponent implements OnInit, OnChanges {
  @Input() isOpen: boolean = false;
  @Input() channelId: string = '';
  @Input() userId: string = '';
  @Input() userNameMapping: { [key: string]: string } = {};
  @Input() profileImageMapping: { [key: string]: string } = {};
  @Input() chatMap: { [key: string]: any } = {};

  @Output() closePanel = new EventEmitter<void>();
  @Output() messageSelected = new EventEmitter<any>();
  @Output() unstarMessage = new EventEmitter<any>();

  starredMessages: any[] = [];
  filteredMessages: any[] = [];
  searchQuery: string = '';
  isLoading: boolean = false;
  page: number = 1;
  limit: number = 20;
  hasMore: boolean = true;
  expandedMessages = new Set<string>();

  constructor(
    private chatService: ChatService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && changes['isOpen'].currentValue) {
      this.refreshMessages();
    }
  }

  refreshMessages() {
    this.starredMessages = [];
    this.page = 1;
    this.hasMore = true;
    this.loadMessages();
  }

  async loadMessages() {
    if (this.isLoading || !this.hasMore || !this.channelId) return;

    this.isLoading = true;
    try {
      const res: any = await this.chatService.getStarredMessages({
        page: this.page,
        limit: this.limit,
        allChannels: false,
        channelId: this.channelId
      });

      if (res?.data) {
        const data = res.data;
        const result = data.starredMessages || data.starredMsgs || 
                      (data.data && (data.data.starredMessages || data.data.starredMsgs)) || 
                       (Array.isArray(data) ? data : []);
        
        // Group merged carousels
        const combined = [...this.starredMessages, ...result];
        const merged: any[] = [];
        const groupMap = new Map<string, any>();

        combined.forEach(record => {
          const msg = record.message || record;
          const groupId = msg.meta?.groupId;
          if (groupId) {
            let existing = groupMap.get(groupId);
            if (existing) {
              const targetMsg = existing.message || existing;
              const currentFiles = getFilesFromMessage(record);
              currentFiles.forEach(f => {
                if (!targetMsg.files) targetMsg.files = [];
                if (!targetMsg.files.some((ef: any) => ef.fileUrl === f.fileUrl)) {
                  targetMsg.files.push(f);
                }
              });
              if (!existing._allIds) existing._allIds = [existing.messageId || existing._id];
              const mid = record.messageId || record._id;
              if (mid && !existing._allIds.includes(mid)) existing._allIds.push(mid);
              
              if (!existing._starRecordIds) existing._starRecordIds = [existing._id || existing.id];
              const sid = record._id || record.id;
              if (sid && !existing._starRecordIds.includes(sid)) existing._starRecordIds.push(sid);
              
              delete existing._processedFiles; // Invalidate cache after merging
            } else {
              const clone = JSON.parse(JSON.stringify(record));
              const cloneMsg = clone.message || clone;
              cloneMsg.files = this.getFilesFromMessage(record);
              clone._allIds = [clone.messageId || clone._id];
              clone._starRecordIds = [clone._id || clone.id];
              clone.currentSlideIndex = 0;
              groupMap.set(groupId, clone);
              merged.push(clone);
            }
          } else {
            merged.push(record);
          }
        });

        this.starredMessages = merged;
        this.filterMessages();
        this.hasMore = result.length === this.limit;
        this.page++;

      }
    } catch (err) {
      console.error("Failed to load starred messages for channel:", err);
    } finally {
      this.isLoading = false;
    }
  }

  filterMessages() {
    if (!this.searchQuery.trim()) {
      this.filteredMessages = this.starredMessages;
    } else {
      const q = this.searchQuery.toLowerCase().trim();
      this.filteredMessages = this.starredMessages.filter(m => {
        const text = (m.message?.message || m.msg || '').toLowerCase();
        const sender = (this.userNameMapping[m.message?.sender_id] || m.message?.name || '').toLowerCase();
        return text.includes(q) || sender.includes(q);
      });
    }
  }

  onSearchChange() {
    this.filterMessages();
  }

  getChatName(message: any): string {
    const channelId = String(message.message?.channel_id || message.channel_id || '').trim();
    const chat = this.chatMap[channelId];

    if (!chat) {
      // Fallback: If sender is someone else, recipient is Me ('You')
      const senderId = message.message?.sender_id || message.sender_id;
      if (senderId && this.userId && senderId !== this.userId) {
        return 'You';
      }
      return 'Chat';
    }
    
    if (chat.groupName) {
      return chat.groupName;
    }

    // Single Chat logic
    const senderId = message.message?.sender_id || message.sender_id;
    if (senderId === this.userId) {
      return chat.displayName || chat.userName || chat.name || 'User';
    } else {
      return 'You';
    }
  }

  onScroll(event: any) {
    const element = event.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50) {
      this.loadMessages();
    }
  }

  onClose() {
    this.closePanel.emit();
  }

  onMessageClick(message: any) {
    this.messageSelected.emit(message);
  }

  onUnstarClick(event: MouseEvent, message: any) {
    event.stopPropagation();
    // Emit unstar event to parent so it can handle API call and state sync
    this.unstarMessage.emit(message);
    
    // Optimistic UI update: Remove the message from the current list immediately
    const messageId = String(message.messageId || message._id || message.id || '');
    this.starredMessages = this.starredMessages.filter(m => {
      const mId = String(m.messageId || m._id || m.id || '');
      return mId !== messageId;
    });
  }

  prevSlide(message: any) {
    const len = this.getFilesFromMessage(message).length;
    if (len <= 1) return;
    const current = message.currentSlideIndex || 0;
    message.currentSlideIndex = current > 0 ? current - 1 : len - 1;
  }

  nextSlide(message: any) {
    const len = this.getFilesFromMessage(message).length;
    if (len <= 1) return;
    const current = message.currentSlideIndex || 0;
    message.currentSlideIndex = current < len - 1 ? current + 1 : 0;
  }

  toggleMessageExpand(messageId: string) {
    if (!messageId) return;
    if (this.expandedMessages.has(messageId)) {
      this.expandedMessages.delete(messageId);
    } else {
      this.expandedMessages.add(messageId);
    }
  }

  isMessageExpanded(messageId: string): boolean {
    if (!messageId) return false;
    return this.expandedMessages.has(messageId);
  }

  isMessageLong(text: string): boolean {
    return text && text.length > 200; // Lower threshold for sidebar panel
  }

  truncateSafely(str: string, maxLen: number): string {
    if (!str || str.length <= maxLen) return str;
    let cut = str.substring(0, maxLen);

    const pipePairs = (cut.match(/\|\|/g) || []).length;
    if (pipePairs % 2 !== 0) {
      const rest = str.substring(maxLen);
      const closeIdx = rest.indexOf('||');
      if (closeIdx !== -1 && closeIdx < 80) {
        cut += rest.substring(0, closeIdx + 2);
      } else {
        const lastOpen = cut.lastIndexOf('||');
        cut = lastOpen > 0 ? cut.substring(0, lastOpen).trim() : cut + '||';
      }
    }

    const openSpoilers = (cut.match(/\[spoiler\]/gi) || []).length;
    const closeSpoilers = (cut.match(/\[\/spoiler\]/gi) || []).length;
    if (openSpoilers > closeSpoilers) {
      const lastOpen = cut.toLowerCase().lastIndexOf('[spoiler]');
      cut = lastOpen > 0 ? cut.substring(0, lastOpen).trim() : cut + '[/spoiler]';
    }

    return cut.trim() + '...';
  }

  getDisplayMessage(text: string, messageId: string): string {
    if (!text) return '';

    let displayText = text;
    if (this.isMessageLong(text) && !this.isMessageExpanded(messageId)) {
      displayText = this.truncateSafely(text, 200);
    }

    displayText = this.escapeHtml(displayText);
    displayText = displayText.replace(/\|\|([\s\S]+?)\|\|/g, '<span class="tg-spoiler" title="Click to reveal">$1</span>');
    displayText = displayText.replace(/\[spoiler\]([\s\S]+?)\[\/spoiler\]/gi, '<span class="tg-spoiler" title="Click to reveal">$1</span>');

    return displayText;
  }

  escapeHtml(text: string): string {
    const element = document.createElement('div');
    element.innerText = text;
    return element.innerHTML;
  }

  @HostListener('click', ['$event'])
  onItemClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const spoilerEl = target?.closest('.tg-spoiler') as HTMLElement;
    if (spoilerEl) {
      event.preventDefault();
      event.stopPropagation();
      const isRevealed = spoilerEl.classList.toggle('revealed');
      spoilerEl.setAttribute('title', isRevealed ? 'Click to hide' : 'Click to reveal');
    }
  }



  // Helpers
  getReplyThumbnail = getReplyThumbnail;
  getReplyMediaType = getReplyMediaType;
  getFilesFromMessage = getFilesFromMessage;
  isMessageRedundantWithFile = isMessageRedundantWithFile;
  isURL = isURL;
  isValidImageUrl = isValidImageUrl;
  getMessageMeta = getMessageMeta;
  isVoiceMessage = isVoiceMessage;

  handleProfileImageError(event: any) {
    event.target.src = 'assets/user-icon.png';
  }

  handleMediaError(event: any, message: any) {
    const type = this.getReplyMediaType(message);
    if (type === 'Video') {
      event.target.src = 'assets/video-file.jpg';
    } else if (type === 'Audio') {
      event.target.src = 'assets/audio-thumbnail.png';
    } else {
      event.target.src = 'assets/fileAttachment.png';
    }
  }

  openUrl(url: string) {
    if (!url) return;
    window.open(url, '_blank');
  }

  hasLinkMeta(message: any): boolean {
    const target = (message.message && typeof message.message === 'object') ? message.message : message;
    const meta = getMessageMeta(target);
    if (!meta) return false;

    const title = (meta.title || '').trim();
    const description = (meta.description || '').trim();
    const image = (meta.image || '').trim();
    const icon = (meta.icon || '').trim();

    const isImageValid = !!(image && !meta.imageBroken);
    const isIconValid = !!(icon && !meta.iconBroken);

    return !!(title || description || isImageValid || isIconValid);
  }

  isIconMode(message: any): boolean {
    const target = (message.message && typeof message.message === 'object') ? message.message : message;
    const meta = getMessageMeta(target);
    if (!meta) return false;

    const image = (meta.image || '').trim();
    const icon = (meta.icon || '').trim();

    const isImageValid = !!(image && !meta.imageBroken);
    const isIconValid = !!(icon && !meta.iconBroken);

    if (!isIconValid) return false;
    if (!isImageValid) return true;
    if (image === icon) return true;
    const lowerImage = image.toLowerCase();
    if (lowerImage.includes('favicon') || lowerImage.endsWith('.ico')) return true;

    return false;
  }

  onPreviewImageError(message: any) {
    const target = (message.message && typeof message.message === 'object') ? message.message : message;
    if (target.meta) {
      target.meta.imageBroken = true;
      this.cdRef.detectChanges();
    }
  }

  onPreviewIconError(message: any) {
    const target = (message.message && typeof message.message === 'object') ? message.message : message;
    if (target.meta) {
      target.meta.iconBroken = true;
      this.cdRef.detectChanges();
    }
  }

  activeAudio: HTMLAudioElement | null = null;
  currentlyPlayingId: string | null = null;
  audioProgress: { [key: string]: { currentTime: number, duration: number } } = {};

  togglePlay(event: MouseEvent, message: any) {
    event.stopPropagation();
    const id = message.messageId || message._id;
    const files = getFilesFromMessage(message);
    const url = files[0]?.fileUrl;

    if (!url) return;

    if (this.currentlyPlayingId === id) {
      if (this.activeAudio) {
        if (this.activeAudio.paused) {
          this.activeAudio.play();
        } else {
          this.activeAudio.pause();
        }
      }
    } else {
      if (this.activeAudio) {
        this.activeAudio.pause();
        this.activeAudio.src = '';
        this.activeAudio.load();
      }
      this.activeAudio = new Audio(url);
      this.currentlyPlayingId = id;
      
      this.activeAudio.addEventListener('loadedmetadata', () => {
        if (this.activeAudio && this.currentlyPlayingId === id) {
          const dur = this.activeAudio.duration;
          this.audioProgress[id] = { currentTime: 0, duration: dur };
          
          const msgObj = message.message || message;
          if (msgObj) {
            if (!msgObj.meta) msgObj.meta = {};
            msgObj.meta.duration = dur;
          }
          this.cdRef.detectChanges();
        }
      });

      this.activeAudio.addEventListener('timeupdate', () => {
        if (this.activeAudio && this.currentlyPlayingId === id) {
          this.audioProgress[id] = {
            currentTime: this.activeAudio.currentTime,
            duration: this.activeAudio.duration
          };
          this.cdRef.detectChanges();
        }
      });

      this.activeAudio.addEventListener('ended', () => {
        if (this.currentlyPlayingId === id) {
          this.currentlyPlayingId = null;
          if (this.audioProgress[id]) this.audioProgress[id].currentTime = 0;
          this.cdRef.detectChanges();
        }
      });

      this.activeAudio.play();
    }
  }

  formatDuration(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }
}
